// ── Radar helper utilities ───────────────────────────────────────────────────
// Pure functions used by StormAtlasRadarPanel.  No React or Mapbox dependencies.

const API_BASE = process.env.REACT_APP_API_BASE ?? "/api";

// Normalize relative API paths to full URLs.
export function normalizeUrl(relPath) {
  if (!relPath) return "";
  if (relPath.startsWith("http://") || relPath.startsWith("https://")) return relPath;
  return `${API_BASE}${relPath}`;
}

// Convert a page-relative URL to absolute using the browser's current origin.
// Mapbox tile workers require absolute URLs; normalizeUrl() returns relative.
export function toAbsoluteUrl(url) {
  if (!url) return url;
  if (url.startsWith("/")) return window.location.origin + url;
  return url;
}

// Parse HH:MM UTC from a radar frame URL or scan ID.
// Accepts both URL form (KCYS20260314_020200_V06.png)
// and bare scan ID    (KCYS20260314_020200_V06)
// and MRMS equivalents.
export function parseFrameTime(urlOrScanId) {
  if (!urlOrScanId) return null;
  const m = urlOrScanId.match(/[A-Z]{4}\d{8}_(\d{2})(\d{2})(\d{2})_V\d+/);
  if (!m) return null;
  return `${m[1]}:${m[2]} UTC`;
}

// Convert {west, south, east, north} → Mapbox image-source coordinates array.
// Order: top-left, top-right, bottom-right, bottom-left
export function boundsToCoords(b) {
  return [
    [b.west, b.north],
    [b.east, b.north],
    [b.east, b.south],
    [b.west, b.south],
  ];
}

// Guard: require all four bound fields to be finite numbers before calling
// boundsToCoords().  Protects crossfade from partially-written sidecar data.
export function hasValidBounds(b) {
  return (
    b != null &&
    Number.isFinite(b.west) &&
    Number.isFinite(b.south) &&
    Number.isFinite(b.east) &&
    Number.isFinite(b.north)
  );
}

// Type-safe wrapper for Mapbox ImageSource.updateImage().
// Verifies the source exists and exposes updateImage before calling it so
// callers don't need to repeat the typeof guard on every updateImage site.
export function safeUpdateImage(source, opts) {
  if (!source || typeof source.updateImage !== "function") return;
  source.updateImage(opts);
}

// Convert the warnings array to a single GeoJSON FeatureCollection.
// Only warnings with Polygon or MultiPolygon geometry are included.
export function warningsToGeoJson(warnings) {
  const features = [];
  for (const w of warnings) {
    let geom = w.geometry;
    if (!geom) continue;
    if (geom.type === "GeometryCollection" && Array.isArray(geom.geometries)) {
      const poly = geom.geometries.find(
        (g) => g.type === "Polygon" || g.type === "MultiPolygon"
      );
      if (!poly) continue;
      geom = poly;
    }
    if (geom.type !== "Polygon" && geom.type !== "MultiPolygon") continue;
    const text    = [w.headline, w.description, w.instruction].filter(Boolean).join("\n");
    const attrs   = parseWarnAttrs(text);
    const obsType = getWarningObsType(text);
    const event   = w.event || w.type || "";
    const displayLabel = buildWarningLabel(
      event,
      attrs.hail          || "",
      attrs.wind          || "",
      obsType,
      attrs.tornadoPossible || false,
    );

    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("warning feature props", {
        id: w.id, event, hazard_type: w.hazard_type,
        hail: attrs.hail, wind: attrs.wind,
        obsType, tornadoPossible: attrs.tornadoPossible,
        displayLabel,
      });
    }

    features.push({
      type: "Feature",
      geometry: geom,
      properties: {
        id:            w.id            || "",
        event,
        severity:      w.severity      || "",
        severity_rank: w.severity_rank ?? 13,
        hazard_type:   w.hazard_type   || "",
        areaDesc:      w.areaDesc      || w.area || "",
        expires:       w.expires       || "",
        hail:          attrs.hail      || "",
        wind:          attrs.wind      || "",
        obsType:       obsType !== "Not specified" ? obsType : "",
        displayLabel,
      },
    });
  }
  // Sort so lower-priority warnings render first (bottom) and higher-priority last (top).
  features.sort(
    (a, b) => (b.properties?.severity_rank ?? 13) - (a.properties?.severity_rank ?? 13),
  );
  return { type: "FeatureCollection", features };
}

// ── Warning detail helpers ──────────────────────────────────────────────────

export function getWarningIcon(hazardType) {
  switch (hazardType) {
    case "tornado": return "\u{1F32A}\uFE0F";
    case "severe":  return "\u26C8\uFE0F";
    case "flood":   return "\u{1F30A}";
    default:        return "\u26A0\uFE0F";
  }
}

export function getWarningObsType(text) {
  if (!text) return "Not specified";
  const t = text.toLowerCase();
  if (t.includes("confirmed tornado") || t.includes("observed")) return "Observed";
  if (t.includes("radar indicated")) return "Radar Indicated";
  return "Not specified";
}

export function getExpiresCountdown(expiresStr, now = Date.now()) {
  if (!expiresStr) return { text: "Unknown", urgency: "default" };
  const exp = new Date(expiresStr).getTime();
  if (Number.isNaN(exp)) return { text: "Unknown", urgency: "default" };
  const diffMs = exp - now;
  if (diffMs <= 0) return { text: "Expired", urgency: "expired" };
  const totalMin = Math.floor(diffMs / 60_000);
  let text;
  if (totalMin < 60) {
    text = `Expires in ${totalMin} min`;
  } else {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    text = m > 0 ? `Expires in ${h}h ${m}m` : `Expires in ${h}h`;
  }
  const urgency = totalMin <= 5 ? "urgent" : totalMin <= 15 ? "soon" : "default";
  return { text, urgency };
}

// Build a compact multi-line label for a warning polygon.
// hail arrives pre-formatted as `1.75"`, wind as `70 mph`.
export function buildWarningLabel(event, hail, wind, obsType, tornadoPossible) {
  const lines = [];
  const isTornado = event.toLowerCase().includes("tornado");

  if (event) lines.push(event);

  // obsType is only meaningful on tornado warnings
  if (obsType && obsType !== "Not specified" && isTornado) lines.push(obsType);

  // Tornado Possible is only relevant on non-tornado warnings (SVR, etc.)
  if (tornadoPossible && !isTornado) lines.push("Tornado Possible");

  if (hail) lines.push(`Hail: ${hail}`);
  if (wind) lines.push(`Wind: ${wind}`);

  return lines.join("\n");
}

// Parse structured hazard attributes from NWS warning text (description field).
// Returns only fields that are present; caller should omit missing ones.
export function parseWarnAttrs(text) {
  if (!text) return {};
  const r = {};
  // Separator handles NWS structured format ("HAIL...1.75 IN") and prose
  // format ("hail to 1.75 inches", "wind gusts to 60 mph").
  const SEP = /(?:[.\s]|\bto\b)+/;
  const hail   = text.match(new RegExp(`hail(?:\\s+size)?${SEP.source}(\\d+\\.?\\d*)\\s*in`, "i"));
  // Wind: primary covers "wind gusts to X mph" and "WIND...X MPH";
  // second fallback covers "X mph wind gusts" (reversed NWS HAZARD line format);
  // third fallback covers "in excess of X mph", "up to X mph", "exceeding X mph".
  const wind   =
    text.match(new RegExp(`wind(?:s)?(?:\\s+gusts?)?${SEP.source}(\\d+)\\s*mph`, "i")) ||
    text.match(/(\d+)\s*mph\s+wind/i) ||
    text.match(/(?:in\s+excess\s+of|up\s+to|exceeding)\s+(\d+)\s*mph/i);
  const damage = text.match(/damage\s+threat[.\s]+([A-Za-z]+)/i);
  if (hail)                             r.hail         = `${hail[1]}"`;
  if (wind)                             r.wind         = `${wind[1]} mph`;
  if (damage)                           r.damageThreat = damage[1].charAt(0).toUpperCase() + damage[1].slice(1).toLowerCase();
  if (/tornado[.\s]+possible/i.test(text)) r.tornadoPossible = true;

  // Coin/object size hail — fallback when no numeric inch measurement is found.
  // Ordered largest → smallest so the first match wins on ambiguous text.
  // Requires "hail" to appear somewhere in the text to avoid false positives.
  if (!r.hail && /\bhail\b/i.test(text)) {
    const COIN_TABLE = [
      [/softball/i,                '4.25"'],
      [/grapefruit/i,              '4.00"'],
      [/baseball/i,                '2.75"'],
      [/tennis[\s-]?ball/i,        '2.50"'],
      [/golf[\s-]?ball/i,          '1.75"'],
      [/ping[\s-]?pong/i,          '1.50"'],
      [/half[\s-]?dollar/i,        '1.25"'],
      [/nickel/i,                  '0.88"'],
      [/penny|dime/i,              '0.75"'],
      [/marble/i,                  '0.50"'],
      [/pea\b/i,                   '0.25"'],
    ];
    for (const [pat, size] of COIN_TABLE) {
      if (pat.test(text)) { r.hail = size; break; }
    }
    // "quarter" last — only in explicit hail context to avoid "quarter mile" collision
    if (!r.hail && /quarter[\s-]?size[d]?\s+hail|hail[^.]*?\bquarter\b|HAIL[.\s]+[^.]*?\bquarter\b/i.test(text)) {
      r.hail = '1.00"';
    }
  }

  // Storm motion — "moving east at 35 mph", "moving NE at 45 mph", etc.
  const COMPASS = "(?:NNE|NNW|SSE|SSW|ENE|ESE|WNW|WSW|NE|NW|SE|SW|N|S|E|W|north(?:north)?(?:east|west)?|south(?:south)?(?:east|west)?|east|west)";
  const motionM = text.match(new RegExp(`moving\\s+(${COMPASS})\\s+at\\s+(\\d+)\\s*mph`, "i"));
  if (motionM) {
    const DIR_EXPAND = {
      N:"North", NE:"Northeast", E:"East", SE:"Southeast",
      S:"South", SW:"Southwest", W:"West", NW:"Northwest",
      NNE:"NNE", NNW:"NNW", SSE:"SSE", SSW:"SSW",
      ENE:"ENE", ESE:"ESE", WNW:"WNW", WSW:"WSW",
    };
    const rawDir = motionM[1].toUpperCase();
    const dir = DIR_EXPAND[rawDir] ||
      (motionM[1].charAt(0).toUpperCase() + motionM[1].slice(1).toLowerCase());
    r.stormMotion = `${dir} at ${motionM[2]} mph`;
  }

  // Visibility — handles NWS structured ("VISIBILITY...LESS THAN 1/4 MILE"),
  // prose numeric ("less than 1 mile visibility"), and prose word-numbers
  // ("less than three miles visibility").
  const VIS_WORDS = {
    "a quarter": "1/4", quarter: "1/4",
    "a half":    "1/2", half:    "1/2",
    a: "1", one: "1", two: "2", three: "3", four: "4", five: "5",
  };
  if (/near[\s-]+zero\s+visib/i.test(text)) {
    r.visibility = "near zero";
  } else {
    const vm =
      text.match(/(?:less\s+than\s+)([\d./]+)\s*miles?\s+visib/i) ||
      text.match(/visib\w*[^.]*?(?:less\s+than\s+)([\d./]+)\s*miles?/i) ||
      text.match(/VISIBILITY[^.]*?(?:LESS\s+THAN\s+)?([\d./]+)\s+MILE/i) ||
      text.match(/(?:less\s+than\s+)(a\s+quarter|quarter|a\s+half|half|one|two|three|four|five)\s+miles?\s+visib/i) ||
      text.match(/visib\w*[^.]*?(?:less\s+than\s+)(a\s+quarter|quarter|a\s+half|half|one|two|three|four|five)\s+miles?/i);
    if (vm) {
      const raw      = vm[1].trim().toLowerCase();
      const resolved = VIS_WORDS[raw] ?? raw;
      const prefix   = /less\s+than/i.test(vm[0]) ? "< " : "";
      r.visibility   = `${prefix}${resolved} mi`;
    }
  }

  return r;
}

// Compute clamped {left, top} for the warning popup so it stays fully inside
// the map container.  cardW/cardH are the assumed max card dimensions.
export function clampPopupPos(clickX, clickY, containerW, containerH,
                              cardW = 260, cardH = 200, offset = 12, margin = 8) {
  let left = clickX + offset;
  let top  = clickY - offset - cardH;     // prefer above the click point
  // Flip right: if card overflows right edge, place left of click
  if (left + cardW + margin > containerW) left = clickX - offset - cardW;
  // Clamp horizontal
  if (left < margin) left = margin;
  if (left + cardW + margin > containerW) left = containerW - cardW - margin;
  // If card overflows top, place it below the click point instead
  if (top < margin) top = clickY + offset;
  // Clamp vertical
  if (top + cardH + margin > containerH) top = containerH - cardH - margin;
  if (top < margin) top = margin;
  return { left, top };
}
